import { FileStatus } from './local-import.enum';
import { ImportFileInfo } from './local-import.interface';

export class Folder {
  name: string;
  files: ImportFileInfo[];
  dirs: { [key: string]: Folder };
  size: number;
  fileCount: number;
  status: FileStatus;
  hasRetryableError: boolean;

  constructor(name: string) {
    this.name = name;
    this.files = [];
    this.dirs = {};
    this.size = 0;
    this.fileCount = 0;
    this.status = FileStatus.InProgress;
  }

  get completedPartsSize(): number {
    let totalCompletedPartsSize = this.files.reduce((sum, file) => {
      return sum + (file.completedPartsSize ?? 0);
    }, 0);

    for (const subFolderKey in this.dirs) {
      totalCompletedPartsSize += this.dirs[subFolderKey].completedPartsSize;
    }

    return totalCompletedPartsSize;
  }

  get dirList(): Folder[] {
    const dirsList = [];

    for (const key in this.dirs) {
      dirsList.push(this.dirs[key]);
    }

    return dirsList;
  }

  addFile(file: ImportFileInfo): void {
    this.files.push(file);
    this.size += file.size;
    this.fileCount += 1;
  }

  getDir(name: string): Folder {
    return this.dirs[name];
  }

  addDir(name: string): Folder {
    this.dirs[name] = new Folder(name);

    return this.dirs[name];
  }

  updateSizeAndFileCount(): number {
    this.size = this.files.reduce((acc, file) => acc + file.size, 0);
    this.fileCount = this.files.length;
    for (const dirName in this.dirs) {
      const dir = this.dirs[dirName];
      this.size += dir.updateSizeAndFileCount();
      this.fileCount += dir.fileCount;
    }
    return this.size;
  }

  updateStatus(): void {
    let allComplete = true;
    let hasError = false;
    this.hasRetryableError = false;

    for (const file of this.files) {
      if (file.status === FileStatus.InProgress) {
        this.status = FileStatus.InProgress;
        return;
      }

      if (file.status === FileStatus.Error) {
        hasError = true;

        if (!file.isDicomFailure) {
          this.hasRetryableError = true;
        }
      }

      if (file.status !== FileStatus.Complete) {
        allComplete = false;
      }
    }

    for (const dirName in this.dirs) {
      const dir = this.dirs[dirName];
      dir.updateStatus();

      if (dir.status === FileStatus.InProgress) {
        this.status = FileStatus.InProgress;
        return;
      }

      if (dir.status === FileStatus.Error) {
        hasError = true;

        if (dir.hasRetryableError) {
          this.hasRetryableError = true;
        }
      }

      if (dir.status !== FileStatus.Complete) {
        allComplete = false;
      }
    }

    if (hasError) {
      this.status = FileStatus.Error;
    } else if (allComplete) {
      this.status = FileStatus.Complete;
    } else {
      this.status = FileStatus.InProgress;
    }
  }
}
